// ============================================================================
// Facebook Messenger OAuth — authorize URL + short-to-long token exchange +
// long-lived page token derivation.
//
// Flow summary (Facebook Login for Pages):
//   1. `buildAuthorizeUrl(...)` → redirect browser to the FB dialog. The
//      platform's PKCE engine (`services/oauth.service.ts`) stores the state
//      row in `connectors_oauth_states`.
//   2. FB returns `?code=...&state=...` to our callback. The platform route
//      calls `exchangeCode(...)` from the shared OAuth service → we receive a
//      short-lived USER access token (~1h).
//   3. `extendToken(...)` swaps that for a long-lived USER token (~60d).
//   4. `getLongLivedPageToken(...)` derives the PAGE access token for a given
//      page id. Page tokens minted from a long-lived user token are themselves
//      non-expiring as long as the user token + page scope stay valid.
//
// References:
//   - https://developers.facebook.com/docs/facebook-login/guides/access-tokens/#extendtoken
//   - https://developers.facebook.com/docs/pages/access-tokens
// ============================================================================

import type { DrizzleD1Database } from 'drizzle-orm/d1'

import {
  buildAuthorizeUrl as coreBuildAuthorizeUrl,
  type BuildAuthorizeUrlInput as CoreBuildAuthorizeUrlInput,
  type BuildAuthorizeUrlResult as CoreBuildAuthorizeUrlResult,
} from '../../services/oauth.service'

// ============================================================================
// Constants
// ============================================================================

/**
 * Facebook OAuth dialog endpoint — Graph API v25.0 (stable since Feb 2026).
 * v19.0 (previous default here) expires 2026-05-21; bumping ahead of time
 * avoids the post-deadline cliff where every authorize call 400s.
 */
export const FB_OAUTH_AUTHORIZE_URL = 'https://www.facebook.com/v25.0/dialog/oauth'
/** Default Graph API base used for token exchange + page-token lookups. Injectable for tests. */
export const FB_GRAPH_API_BASE = 'https://graph.facebook.com/v25.0'

/**
 * Default scope set for the Messenger adapter.
 *
 * - `pages_messaging` — send/receive messages via Messenger Platform.
 * - `pages_show_list` — list the pages the user manages directly
 *   (/me/accounts returns these).
 * - `pages_manage_metadata` — subscribe the page to webhooks.
 * - `pages_read_engagement` — read the page's own id/name + engagement metrics
 *   so we can resolve the /me response consistently.
 * - `business_management` — traverse businesses owned by the user and list
 *   their owned/client pages. Without this scope FB hides pages the user
 *   only manages via a Business Portfolio (e.g. agency-style access) from
 *   /me/accounts, so the page picker missed the second/third page on real
 *   multi-page users.
 */
export const FB_MESSENGER_DEFAULT_SCOPES = [
  'pages_messaging',
  'pages_show_list',
  'pages_manage_metadata',
  'pages_read_engagement',
  'business_management',
] as const

// ============================================================================
// buildAuthorizeUrl — thin FB-flavoured wrapper around the shared PKCE engine
// ============================================================================

/**
 * Input for `buildAuthorizeUrl`. Mirrors the shared engine's input but drops
 * `authorizeUrl` (hard-coded to FB) and lets callers override scopes if they
 * need a subset (e.g. read-only pages integration).
 */
export type FacebookMessengerAuthorizeInput = Omit<
  CoreBuildAuthorizeUrlInput,
  'authorizeUrl' | 'scopes'
> & {
  /** Override the default FB Messenger scopes. Defaults to {@link FB_MESSENGER_DEFAULT_SCOPES}. */
  scopes?: readonly string[]
}

export type FacebookMessengerAuthorizeResult = CoreBuildAuthorizeUrlResult & {
  /**
   * PKCE code verifier that will later be submitted to Facebook's token
   * endpoint. Persisted on the state row by the core engine — surfaced here
   * only so tests can assert the engine was called correctly.
   */
  codeVerifier?: string
}

/**
 * Build the Facebook OAuth authorize URL + persist a PKCE state row.
 *
 * The shared engine does all the heavy lifting (state generation, PKCE,
 * state-row insert). This wrapper only picks the FB endpoint + scopes.
 */
export async function buildAuthorizeUrl(
  opts: FacebookMessengerAuthorizeInput,
  db: DrizzleD1Database,
  now: Date = new Date(),
): Promise<FacebookMessengerAuthorizeResult> {
  const scopes = (opts.scopes && opts.scopes.length > 0
    ? opts.scopes
    : FB_MESSENGER_DEFAULT_SCOPES) as readonly string[]

  return coreBuildAuthorizeUrl(
    {
      ...opts,
      authorizeUrl: FB_OAUTH_AUTHORIZE_URL,
      scopes: [...scopes],
    },
    db,
    now,
  )
}

// ============================================================================
// extendToken — short-lived user token → long-lived (~60d) user token
// ============================================================================

export interface ExtendTokenInput {
  /** Short-lived FB user token (typically returned by `exchangeCode`). */
  shortLivedToken: string
  /** FB app id. */
  clientId: string
  /** FB app secret. Required: FB rejects extend requests without it. */
  clientSecret: string
  /** Override the Graph API base URL (tests inject a stub). */
  graphApiBase?: string
}

export interface ExtendedUserToken {
  /** Long-lived user access token. */
  accessToken: string
  /** Seconds until expiry — typically ~5,184,000 (~60 days). May be omitted by FB. */
  expiresIn?: number
  /** Token type echoed by FB — usually `bearer`. */
  tokenType?: string
  /** Absolute expiry derived from `expires_in` + call time. ISO 8601 UTC. */
  expiresAt?: string
}

/**
 * Exchange a short-lived user token for a long-lived one via
 * `GET /oauth/access_token?grant_type=fb_exchange_token`.
 *
 * FB's endpoint accepts both GET query-string and POST-body variants; we use
 * GET because the endpoint treats the `fb_exchange_token` param as an
 * identifier-ish value (documented as a query param).
 */
export async function extendToken(
  input: ExtendTokenInput,
  fetchImpl: typeof fetch = fetch,
  now: Date = new Date(),
): Promise<ExtendedUserToken> {
  assertNonEmpty(input.shortLivedToken, 'shortLivedToken')
  assertNonEmpty(input.clientId, 'clientId')
  assertNonEmpty(input.clientSecret, 'clientSecret')

  const base = input.graphApiBase ?? FB_GRAPH_API_BASE
  // Client secret MUST go in the request body, not the URL. Cloudflare edge
  // logs (and every proxy along the path) capture full URLs; a leaked secret
  // means anyone can mint long-lived tokens for the app until it's rotated.
  const params = new URLSearchParams({
    grant_type: 'fb_exchange_token',
    client_id: input.clientId,
    client_secret: input.clientSecret,
    fb_exchange_token: input.shortLivedToken,
  })

  const resp = await fetchImpl(`${base}/oauth/access_token`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!resp.ok) {
    const errText = await safeReadText(resp)
    console.error(
      '[connectors/facebook-messenger/oauth] extendToken failed',
      resp.status,
      redactSecrets(errText).slice(0, 500),
    )
    throw new FacebookMessengerOAuthError(
      'EXTEND_FAILED',
      `provider returned status ${resp.status}`,
    )
  }

  const body = (await resp.json().catch(() => null)) as
    | { access_token?: string; expires_in?: number; token_type?: string }
    | null
  if (!body || typeof body.access_token !== 'string' || body.access_token.length === 0) {
    throw new FacebookMessengerOAuthError(
      'EXTEND_FAILED',
      'provider did not return access_token',
    )
  }

  const expiresIn = typeof body.expires_in === 'number' ? body.expires_in : undefined
  const expiresAt =
    typeof expiresIn === 'number'
      ? new Date(now.getTime() + expiresIn * 1000).toISOString()
      : undefined

  return {
    accessToken: body.access_token,
    expiresIn,
    tokenType: typeof body.token_type === 'string' ? body.token_type : undefined,
    expiresAt,
  }
}

// ============================================================================
// getLongLivedPageToken — derive a page token from a long-lived user token
// ============================================================================

export interface GetLongLivedPageTokenInput {
  /**
   * Long-lived user access token (returned by {@link extendToken}). Passing
   * a short-lived token also works but the resulting page token then expires
   * in ~1h, defeating the point — callers should always extend first.
   */
  userAccessToken: string
  /** Facebook page id the caller wants to message through. */
  pageId: string
  /** Override the Graph API base URL (tests inject a stub). */
  graphApiBase?: string
}

export interface LongLivedPageToken {
  /** Non-expiring page access token. */
  accessToken: string
  /** Echoed page id from the response (same as input unless FB rewrites it). */
  pageId: string
  /** Optional page display name if FB returned it (we don't request it by default). */
  name?: string
}

/**
 * Fetch the page access token via `GET /{page_id}?fields=access_token`.
 *
 * Requires the caller to hold `pages_show_list` + page admin role; the token
 * response from FB is the canonical source for the page access token.
 */
export async function getLongLivedPageToken(
  input: GetLongLivedPageTokenInput,
  fetchImpl: typeof fetch = fetch,
): Promise<LongLivedPageToken> {
  assertNonEmpty(input.userAccessToken, 'userAccessToken')
  assertNonEmpty(input.pageId, 'pageId')

  const base = input.graphApiBase ?? FB_GRAPH_API_BASE
  // Pass the long-lived USER token via `Authorization: Bearer` so it stays
  // out of Cloudflare edge logs + proxy traces. (Graph API v11+ accepts the
  // Bearer header for any endpoint that would otherwise take `access_token`
  // in the query string.) Leaking a user token is roughly equivalent to
  // phishing the page owner; we cannot afford to leave it in URLs.
  const url = `${base}/${encodeURIComponent(input.pageId)}?fields=access_token,name`
  const resp = await fetchImpl(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${input.userAccessToken}`,
    },
  })

  if (!resp.ok) {
    const errText = await safeReadText(resp)
    console.error(
      '[connectors/facebook-messenger/oauth] getLongLivedPageToken failed',
      resp.status,
      redactSecrets(errText).slice(0, 500),
    )
    throw new FacebookMessengerOAuthError(
      'PAGE_TOKEN_FAILED',
      `provider returned status ${resp.status}`,
    )
  }

  const body = (await resp.json().catch(() => null)) as
    | { access_token?: string; id?: string; name?: string }
    | null
  if (!body || typeof body.access_token !== 'string' || body.access_token.length === 0) {
    throw new FacebookMessengerOAuthError(
      'PAGE_TOKEN_FAILED',
      'provider did not return access_token',
    )
  }

  return {
    accessToken: body.access_token,
    pageId: typeof body.id === 'string' && body.id.length > 0 ? body.id : input.pageId,
    name: typeof body.name === 'string' ? body.name : undefined,
  }
}

// ============================================================================
// listUserPages — GET /me/accounts
// ============================================================================

export interface ListUserPagesInput {
  /** Long-lived (or short-lived) USER access token from `extendToken` / exchange. */
  userAccessToken: string
  /** Override the Graph API base URL (tests inject a stub). */
  graphApiBase?: string
}

export interface UserPage {
  id: string
  name: string
  category?: string
  avatarUrl?: string
  /** Per-page access token returned inline (we still call getLongLivedPageToken
   *  later to ensure non-expiring semantics when the caller holds a long-lived
   *  user token). */
  accessToken?: string
}

/**
 * List pages the user admins via `GET /me/accounts`. Requires `pages_show_list`.
 * The response includes a page access token per row when the scope is granted —
 * we return `accessToken` so the frontend picker can surface "linked" state.
 *
 * Handles pagination: FB defaults to 25 pages per response and hands back
 * `paging.cursors.after` / `paging.next` when there are more. We walk the
 * cursor up to a safety cap so a malicious/broken provider can't spin us.
 */
export async function listUserPages(
  input: ListUserPagesInput,
  fetchImpl: typeof fetch = fetch,
): Promise<UserPage[]> {
  assertNonEmpty(input.userAccessToken, 'userAccessToken')
  const base = input.graphApiBase ?? FB_GRAPH_API_BASE
  const MAX_PAGES = 10 // 10 × 100 per page = 1000 FB pages is more than enough
  const seen = new Map<string, UserPage>()

  const authHeader = { Authorization: `Bearer ${input.userAccessToken}` }

  // --- 1. /me/accounts — pages the user directly admins ---------------------
  let nextUrl: string | null =
    `${base}/me/accounts?fields=id,name,category,access_token,picture.type(large),tasks&limit=100`
  for (let i = 0; i < MAX_PAGES && nextUrl; i++) {
    const resp: Response = await fetchImpl(nextUrl, {
      method: 'GET',
      headers: { Accept: 'application/json', ...authHeader },
    })
    if (!resp.ok) {
      const errText = await safeReadText(resp)
      console.error(
        '[connectors/facebook-messenger/oauth] listUserPages /me/accounts failed',
        resp.status,
        redactSecrets(errText).slice(0, 500),
      )
      throw new FacebookMessengerOAuthError(
        'LIST_PAGES_FAILED',
        `provider returned status ${resp.status}`,
      )
    }
    const body = (await resp.json().catch(() => null)) as
      | {
          data?: Array<{
            id?: string
            name?: string
            category?: string
            access_token?: string
            picture?: FacebookPagePicture
            tasks?: string[]
          }>
          paging?: { next?: string }
        }
      | null
    if (!body || !Array.isArray(body.data)) {
      throw new FacebookMessengerOAuthError(
        'LIST_PAGES_FAILED',
        'provider did not return pages list',
      )
    }
    for (const p of body.data) {
      if (typeof p.id !== 'string' || !p.id || typeof p.name !== 'string') continue
      if (seen.has(p.id)) continue
      seen.set(p.id, {
        id: p.id,
        name: p.name,
        category: p.category,
        avatarUrl: extractAvatarUrl(p.picture),
        accessToken: p.access_token,
      })
    }
    nextUrl = typeof body.paging?.next === 'string' ? body.paging.next : null
  }

  // --- 2. Pages ASSIGNED to the user via a Business Portfolio.
  // `/me/assigned_pages` covers the multi-brand / agency-style access model
  // where the user doesn't directly admin the Page but has a role granted
  // through a business. No extra scope required — it relies on the same
  // `pages_show_list` consent as /me/accounts. Failures are swallowed so
  // tokens that just can't reach this edge don't break the picker.
  try {
    await collectFromEdge(
      base,
      '/me/assigned_pages?fields=id,name,category,access_token,picture.type(large),tasks&limit=100',
      input.userAccessToken,
      fetchImpl,
      seen,
      MAX_PAGES,
      'assigned_pages',
    )
  } catch (err) {
    console.warn(
      '[connectors/facebook-messenger/oauth] /me/assigned_pages traversal failed (continuing with direct pages)',
      err instanceof Error ? err.message : 'unknown',
    )
  }

  // --- 3. Businesses the user has access to — each owns/claims its own pages.
  // Requires the `business_management` scope which Meta gates behind App
  // Review for every app (even dev mode). Kept as a best-effort extra pass;
  // logged and skipped when unavailable.
  try {
    const businessIds = await fetchBusinessIds(base, input.userAccessToken, fetchImpl)
    for (const bizId of businessIds) {
      for (const edge of ['owned_pages', 'client_pages'] as const) {
        await collectBusinessPages(
          base,
          bizId,
          edge,
          input.userAccessToken,
          fetchImpl,
          seen,
          MAX_PAGES,
        )
      }
    }
  } catch (err) {
    console.warn(
      '[connectors/facebook-messenger/oauth] business-scoped page traversal failed (continuing with direct pages only)',
      err instanceof Error ? err.message : 'unknown',
    )
  }

  const collected = Array.from(seen.values())
  // Dev visibility: if the count looks low compared to what the user
  // expects, logs show the actual count + ids so support can diff against
  // the Facebook Business Settings view.
  console.info(
    '[connectors/facebook-messenger/oauth] listUserPages returned',
    collected.length,
    'pages:',
    collected.map((p) => `${p.id} (${p.name})`).join(', '),
  )

  return collected
}

async function fetchBusinessIds(
  base: string,
  userAccessToken: string,
  fetchImpl: typeof fetch,
): Promise<string[]> {
  const url = `${base}/me/businesses?fields=id,name&limit=100`
  const resp = await fetchImpl(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${userAccessToken}`,
    },
  })
  if (!resp.ok) {
    const errText = await safeReadText(resp)
    console.info(
      '[connectors/facebook-messenger/oauth] /me/businesses not available',
      resp.status,
      redactSecrets(errText).slice(0, 300),
    )
    return []
  }
  const body = (await resp.json().catch(() => null)) as
    | { data?: Array<{ id?: string; name?: string }> }
    | null
  if (!body || !Array.isArray(body.data)) return []
  console.info(
    '[connectors/facebook-messenger/oauth] /me/businesses returned',
    body.data.length,
    'businesses:',
    body.data.map((b) => `${b.id} (${b.name ?? ''})`).join(', '),
  )
  return body.data
    .map((b) => (typeof b.id === 'string' ? b.id : null))
    .filter((x): x is string => !!x)
}

async function collectFromEdge(
  base: string,
  startPath: string,
  userAccessToken: string,
  fetchImpl: typeof fetch,
  out: Map<string, UserPage>,
  maxPages: number,
  label: string,
): Promise<void> {
  let nextUrl: string | null = `${base}${startPath}`
  let added = 0
  for (let i = 0; i < maxPages && nextUrl; i++) {
    const resp: Response = await fetchImpl(nextUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${userAccessToken}`,
      },
    })
    if (!resp.ok) {
      const errText = await safeReadText(resp)
      console.info(
        `[connectors/facebook-messenger/oauth] ${label} not available`,
        resp.status,
        redactSecrets(errText).slice(0, 300),
      )
      return
    }
    const body = (await resp.json().catch(() => null)) as
      | {
          data?: Array<{
            id?: string
            name?: string
            category?: string
            access_token?: string
            picture?: FacebookPagePicture
            tasks?: string[]
          }>
          paging?: { next?: string }
        }
      | null
    if (!body || !Array.isArray(body.data)) return
    for (const p of body.data) {
      if (typeof p.id !== 'string' || !p.id || typeof p.name !== 'string') continue
      if (out.has(p.id)) continue
      out.set(p.id, {
        id: p.id,
        name: p.name,
        category: p.category,
        avatarUrl: extractAvatarUrl(p.picture),
        accessToken: p.access_token,
      })
      added++
    }
    nextUrl = typeof body.paging?.next === 'string' ? body.paging.next : null
  }
  console.info(
    `[connectors/facebook-messenger/oauth] ${label} added ${added} new pages`,
  )
}

async function collectBusinessPages(
  base: string,
  businessId: string,
  edge: 'owned_pages' | 'client_pages',
  userAccessToken: string,
  fetchImpl: typeof fetch,
  out: Map<string, UserPage>,
  maxPages: number,
): Promise<void> {
  let nextUrl: string | null =
    `${base}/${encodeURIComponent(businessId)}/${edge}?fields=id,name,category,access_token,picture.type(large),tasks&limit=100`
  for (let i = 0; i < maxPages && nextUrl; i++) {
    const resp: Response = await fetchImpl(nextUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${userAccessToken}`,
      },
    })
    if (!resp.ok) {
      // Business edges 400 when the caller lacks Business Portfolio admin
      // on that specific business — not fatal, just skip this business.
      return
    }
    const body = (await resp.json().catch(() => null)) as
      | {
          data?: Array<{
            id?: string
            name?: string
            category?: string
            access_token?: string
            picture?: FacebookPagePicture
            tasks?: string[]
          }>
          paging?: { next?: string }
        }
      | null
    if (!body || !Array.isArray(body.data)) return
    for (const p of body.data) {
      if (typeof p.id !== 'string' || !p.id || typeof p.name !== 'string') continue
      if (out.has(p.id)) continue
      out.set(p.id, {
        id: p.id,
        name: p.name,
        category: p.category,
        avatarUrl: extractAvatarUrl(p.picture),
        accessToken: p.access_token,
      })
    }
    nextUrl = typeof body.paging?.next === 'string' ? body.paging.next : null
  }
}

type FacebookPagePicture =
  | string
  | {
      data?: {
        url?: string
      }
    }

function extractAvatarUrl(picture: FacebookPagePicture | undefined): string | undefined {
  if (!picture) return undefined
  if (typeof picture === 'string') return picture
  const url = picture.data?.url
  return typeof url === 'string' && url.length > 0 ? url : undefined
}

// ============================================================================
// subscribeApp — POST /{page_id}/subscribed_apps
// ============================================================================

/**
 * Default set of Messenger webhook fields to subscribe. Mirrors what we want
 * Facebook to push to our app-level callback URL.
 */
export const FB_MESSENGER_SUBSCRIBED_FIELDS = [
  'messages',
  'messaging_postbacks',
  'message_deliveries',
  'message_reads',
  'messaging_optins',
  'message_echoes',
] as const

export interface SubscribeAppWebhookInput {
  /** Facebook App id. */
  clientId: string
  /** Facebook App secret. Used only to build an App Access Token. */
  clientSecret: string
  /** Public callback URL configured in the Meta App webhook product. */
  callbackUrl: string
  /** Verify token Meta uses for callback validation. */
  verifyToken: string
  /** Override the fields the app-level webhook receives. Defaults to {@link FB_MESSENGER_SUBSCRIBED_FIELDS}. */
  subscribedFields?: readonly string[]
  /** Override the Graph API base URL (tests inject a stub). */
  graphApiBase?: string
}

export interface SubscribeAppInput {
  /** Page access token (from {@link getLongLivedPageToken}). Carries the permission to bind the page to an app. */
  pageAccessToken: string
  /** Facebook page id. */
  pageId: string
  /** Override the fields the page sends to our webhook. Defaults to {@link FB_MESSENGER_SUBSCRIBED_FIELDS}. */
  subscribedFields?: readonly string[]
  /** Override the Graph API base URL (tests inject a stub). */
  graphApiBase?: string
}

/**
 * Subscribe our Facebook App to a page's webhook events.
 *
 * Unlike most providers, Messenger's webhook URL is configured ONCE at the
 * app dashboard — Facebook then routes every subscribed page's events to that
 * single callback. Calling `POST /{page_id}/subscribed_apps` wires a specific
 * page into that app-level subscription using the page's access token.
 *
 * Reference: https://developers.facebook.com/docs/messenger-platform/webhooks#subscribe-app-to-page
 */
export async function subscribeApp(
  input: SubscribeAppInput,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  assertNonEmpty(input.pageAccessToken, 'pageAccessToken')
  assertNonEmpty(input.pageId, 'pageId')

  const base = input.graphApiBase ?? FB_GRAPH_API_BASE
  const fields = (input.subscribedFields && input.subscribedFields.length > 0
    ? input.subscribedFields
    : FB_MESSENGER_SUBSCRIBED_FIELDS) as readonly string[]

  const body = new URLSearchParams({ subscribed_fields: fields.join(',') })
  const url = `${base}/${encodeURIComponent(input.pageId)}/subscribed_apps`
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${input.pageAccessToken}`,
    },
    body: body.toString(),
  })

  if (!resp.ok) {
    const errText = await safeReadText(resp)
    console.error(
      '[connectors/facebook-messenger/oauth] subscribeApp failed',
      resp.status,
      redactSecrets(errText).slice(0, 500),
    )
    throw new FacebookMessengerOAuthError(
      'SUBSCRIBE_APP_FAILED',
      `provider returned status ${resp.status}`,
    )
  }

  const parsed = (await resp.json().catch(() => null)) as { success?: boolean } | null
  if (!parsed || parsed.success !== true) {
    throw new FacebookMessengerOAuthError(
      'SUBSCRIBE_APP_FAILED',
      'provider did not report success',
    )
  }
}

// ============================================================================
// subscribeAppWebhook — POST /{app_id}/subscriptions
// ============================================================================

/**
 * Ensure the Meta App-level Page webhook includes every Messenger field we
 * expect. Page-level `subscribed_apps` alone is not enough: Meta only delivers
 * a field when it is enabled in BOTH places. In particular, replies sent from
 * Meta Business Suite require `message_echoes` here.
 */
export async function subscribeAppWebhook(
  input: SubscribeAppWebhookInput,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  assertNonEmpty(input.clientId, 'clientId')
  assertNonEmpty(input.clientSecret, 'clientSecret')
  assertNonEmpty(input.callbackUrl, 'callbackUrl')
  assertNonEmpty(input.verifyToken, 'verifyToken')

  const base = input.graphApiBase ?? FB_GRAPH_API_BASE
  const fields = input.subscribedFields && input.subscribedFields.length > 0
    ? input.subscribedFields
    : FB_MESSENGER_SUBSCRIBED_FIELDS

  const body = new URLSearchParams({
    object: 'page',
    callback_url: input.callbackUrl,
    verify_token: input.verifyToken,
    fields: fields.join(','),
  })
  const url = `${base}/${encodeURIComponent(input.clientId)}/subscriptions`
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${input.clientId}|${input.clientSecret}`,
    },
    body: body.toString(),
  })

  if (!resp.ok) {
    const errText = await safeReadText(resp)
    console.error(
      '[connectors/facebook-messenger/oauth] subscribeAppWebhook failed',
      resp.status,
      redactSecrets(errText).slice(0, 500),
    )
    throw new FacebookMessengerOAuthError(
      'SUBSCRIBE_APP_FAILED',
      `provider returned status ${resp.status}`,
    )
  }

  const parsed = (await resp.json().catch(() => null)) as { success?: boolean } | null
  if (!parsed || parsed.success !== true) {
    throw new FacebookMessengerOAuthError(
      'SUBSCRIBE_APP_FAILED',
      'provider did not report success',
    )
  }
}

// ============================================================================
// Errors
// ============================================================================

export type FacebookMessengerOAuthErrorCode =
  | 'EXTEND_FAILED'
  | 'PAGE_TOKEN_FAILED'
  | 'LIST_PAGES_FAILED'
  | 'SUBSCRIBE_APP_FAILED'
  | 'INVALID_INPUT'

export class FacebookMessengerOAuthError extends Error {
  constructor(
    public readonly code: FacebookMessengerOAuthErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'FacebookMessengerOAuthError'
  }
}

// ============================================================================
// Helpers
// ============================================================================

function assertNonEmpty(value: string | undefined | null, field: string): void {
  if (typeof value !== 'string' || value.length === 0) {
    throw new FacebookMessengerOAuthError('INVALID_INPUT', `${field} is required`)
  }
}

async function safeReadText(resp: Response): Promise<string> {
  try {
    return await resp.text()
  } catch {
    return ''
  }
}

// Match the patterns FB echoes back in error bodies so we never commit them
// to Workers tail logs. FB error envelopes frequently contain the full URL of
// the failing request, including the `access_token` query param or the raw
// token inside a debug message; the capture groups below strip those.
const SECRET_PATTERNS: ReadonlyArray<RegExp> = [
  /(access_token=)[^&\s"']+/gi,
  /(client_secret=)[^&\s"']+/gi,
  /(fb_exchange_token=)[^&\s"']+/gi,
  /(Bearer\s+)[A-Za-z0-9._\-|]+/gi,
]

/**
 * Redact any FB-shaped secrets from a log string. Exported for reuse in other
 * adapter files (send.ts, profile.ts) so the sanitisation policy stays in one
 * place.
 */
export function redactSecrets(raw: string): string {
  let out = raw
  for (const pattern of SECRET_PATTERNS) {
    out = out.replace(pattern, '$1REDACTED')
  }
  return out
}
